import { useState, useEffect } from 'react';
import {
  useNotify,
  Button,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  TextField as MuiTextField,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { PageWrapper } from '../../components/PageWrapper';
import { baseUrl } from '../../customDataProvider';

interface FormSettingRow {
  id: number;
  formId: number;
  formName: string;
  active: boolean;
  notifySubmitter: boolean;
  recipients: string;
  defectNotificationRecipients: string;
}

const getAccessToken = () => localStorage.getItem('accessToken') || '';
const getTenantCode = () => {
  const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
  if (!tenantsEnabled) return 'default';
  const currentTenant = localStorage.getItem('currentTenant');
  try { return currentTenant ? JSON.parse(currentTenant).code : 'default'; } catch { return 'default'; }
};

const StatusChip = ({ active }: { active: boolean }) => (
  <Chip
    label={active ? "Active" : "Inactive"}
    color={active ? "success" : "default"}
    size="small"
  />
);

const EmailSubmitterChip = ({ notifySubmitter }: { notifySubmitter: boolean }) => (
  <Chip
    label={notifySubmitter ? "Email Submitter" : "Don't Email"}
    color={notifySubmitter ? "info" : "default"}
    size="small"
  />
);

const EditFormSettingDialog = ({ 
  open, 
  onClose, 
  record, 
  onSave 
}: { 
  open: boolean;
  onClose: () => void;
  record: FormSettingRow | null;
  onSave: (data: FormSettingRow) => void;
}) => {
  const [formData, setFormData] = useState<FormSettingRow | null>(null);

  useEffect(() => {
    if (record) {
      setFormData({ ...record });
    }
  }, [record]);

  const handleSave = () => {
    if (formData) {
      onSave(formData);
    }
  };

  if (!formData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Form Settings: {formData.formName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData(prev => prev ? { ...prev, active: e.target.checked } : null)}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1">Form Active</Typography>
                <Typography variant="caption" color="textSecondary">
                  Allow users to submit this form
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.notifySubmitter}
                onChange={(e) => setFormData(prev => prev ? { ...prev, notifySubmitter: e.target.checked } : null)}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1">Email Submitter</Typography>
                <Typography variant="caption" color="textSecondary">
                  Send email notification to the person who submitted the form
                </Typography>
              </Box>
            }
          />
          
          <MuiTextField
            label="Additional Recipients"
            value={formData.recipients || ''}
            onChange={(e) => setFormData(prev => prev ? { ...prev, recipients: e.target.value } : null)}
            fullWidth
            multiline
            rows={3}
            placeholder="Enter email addresses separated by commas"
            helperText="Additional email addresses to notify when this form is submitted"
          />

          <MuiTextField
            label="Defect Notification Recipients"
            value={formData.defectNotificationRecipients || ''}
            onChange={(e) => setFormData(prev => prev ? { ...prev, defectNotificationRecipients: e.target.value } : null)}
            fullWidth
            multiline
            rows={3}
            placeholder="Enter email addresses separated by commas"
            helperText="Email addresses to notify when a submission has a minor or major defect"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TenantFormSettings = () => {
  const [data, setData] = useState<FormSettingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState<{ open: boolean; record: FormSettingRow | null }>({
    open: false,
    record: null,
  });
  const notify = useNotify();

  const loadData = async () => {
    try {
      setLoading(true);
      const tenantCode = getTenantCode();
      const res = await fetch(`${baseUrl}/tenant-form-settings/${tenantCode}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Accept': 'application/json',
          'x-tenant-code': tenantCode,
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      const formattedData = result.map((item: any, index: number) => ({
        id: item.formId, // Use formId as the React Admin ID
        ...item,
      }));
      setData(formattedData);
    } catch (error) {
      console.error('Error loading form settings:', error);
      notify('Failed to load form settings', { type: 'error' });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleEditClick = (record: FormSettingRow) => {
    setEditDialog({ open: true, record });
  };

  const handleSave = async (formData: FormSettingRow) => {
    try {
      const tenantCode = getTenantCode();
      const res = await fetch(`${baseUrl}/tenant-form-settings/${tenantCode}/${formData.formId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
          'x-tenant-code': tenantCode,
        },
        body: JSON.stringify({
          active: formData.active,
          notifySubmitter: formData.notifySubmitter,
          recipients: formData.recipients,
          defectNotificationRecipients: formData.defectNotificationRecipients,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      notify('Settings saved successfully', { type: 'success' });
      setEditDialog({ open: false, record: null });

      // Update the data in state
      setData(prev => prev.map(item =>
        item.formId === formData.formId ? formData : item
      ));

    } catch (error) {
      console.error('Error saving form settings:', error);
      notify('Failed to save settings', { type: 'error' });
    }
  };

  if (loading) {
    return <PageWrapper title="Form Settings">Loading...</PageWrapper>;
  }

  return (
    <PageWrapper title="Form Settings">
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Email Notification Settings
        </Typography>

        {data.length === 0 ? (
          <Typography color="textSecondary">
            No forms found for this tenant.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Form Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Email Submitter</TableCell>
                  <TableCell>Additional Recipients</TableCell>
                  <TableCell>Defect Notification Recipients</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((record) => (
                  <TableRow
                    key={record.formId}
                    onClick={() => handleEditClick(record)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2">{record.formName}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip active={record.active} />
                    </TableCell>
                    <TableCell>
                      <EmailSubmitterChip notifySubmitter={record.notifySubmitter} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {record.recipients || 'None'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {record.defectNotificationRecipients || 'None'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <EditFormSettingDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, record: null })}
        record={editDialog.record}
        onSave={handleSave}
      />
    </PageWrapper>
  );
};

export default TenantFormSettings;


